'use client';

// ═══════════════════════════════════════════════════════════════════════
// PendingReviewLayer — global mount for the manual-review review stack.
//
// Two surfaces work together:
//   • SideSlideAlert (bottom-right) — ephemeral "new review" toast that
//     previews the top candidate set. Shows only when the panel is in
//     'hidden' state, so once the user opens the panel the slide stops
//     popping up (the panel itself is the indicator).
//   • PendingReviewPanel — right-pinned, non-modal column. Stays open
//     across navigation. Can be collapsed to a thin right-edge strip
//     so the user can keep clicking around the site without the panel
//     covering content.
// ═══════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { usePendingReviewQueue } from '@/hooks/usePendingReviewQueue';
import PendingReviewSideSlide from './PendingReviewSideSlide';
import PendingReviewPanel, { type PanelMode } from './PendingReviewPanel';

const PANEL_MODE_STORAGE_KEY = 'pendingReviewPanel:mode';

/** Read the persisted panel mode at mount. Survives navigation /
 *  reload so a user who minimized the panel doesn't have it pop
 *  expanded again on every page change. Values outside the union
 *  fall back to 'hidden'. */
function loadInitialMode(): PanelMode {
  if (typeof window === 'undefined') return 'hidden';
  try {
    const v = window.localStorage.getItem(PANEL_MODE_STORAGE_KEY);
    if (v === 'expanded' || v === 'collapsed') return v;
  } catch { /* ignore */ }
  return 'hidden';
}

export default function PendingReviewLayer() {
  const queue = usePendingReviewQueue();
  const [panelMode, setPanelMode] = useState<PanelMode>(loadInitialMode);

  // Persist mode across navigation. 'hidden' is the default so we don't
  // need to write it — but writing keeps the state consistent if the
  // user closes the panel on one page and refreshes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(PANEL_MODE_STORAGE_KEY, panelMode); } catch { /* ignore */ }
  }, [panelMode]);

  // Side-slide eligibility:
  //   • The queue has at least one item
  //   • The current top item isn't already dismissed by the user
  //   • The panel is hidden (otherwise the panel itself shows the queue)
  //   • For BUY reviews: the portfolio actually has a free slot to act on
  //     it (slotsAvailable > 0). When slots are full, the popup would
  //     just be noise — any approval skips with max_concurrent_hit and
  //     stays in the queue. User can still open the panel manually to
  //     view / clean up the backlog.
  const current = queue.current;
  const buyWithNoSlot = current != null
    && current.action === 'buy'
    && current.slotsAvailable === 0;
  const showSideSlide = panelMode === 'hidden' && current != null && !buyWithNoSlot;

  return (
    <>
      {showSideSlide && queue.current && (
        <PendingReviewSideSlide
          review={queue.current}
          moreCount={queue.moreCount}
          onExpand={() => setPanelMode('expanded')}
          onDismiss={() => queue.dismissCurrent()}
          onSnooze={async (minutes) => {
            if (!queue.current) return;
            // Server-side snooze terminates the review row + pauses
            // ALL trades (buy or sell) for the portfolio for N min.
            // Capture id locally so a queue refresh between click and
            // settle doesn't lose the target.
            const id = queue.current.id;
            await queue.reject(id, { resolution: 'snooze', snoozeMinutes: minutes });
          }}
        />
      )}
      <PendingReviewPanel
        queue={queue.queue}
        mode={panelMode}
        onModeChange={(mode) => {
          // When the user closes the panel ('hidden'), clear the
          // dismissed-set so the side-slide can re-pop for any item
          // still in the queue. Otherwise dismiss markers from before
          // they opened the panel would suppress the slide forever.
          if (mode === 'hidden') queue.resetDismissed();
          setPanelMode(mode);
        }}
        onApprove={queue.approve}
        onReject={queue.reject}
      />
    </>
  );
}
